import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Target, Eye, Users, Globe,
  ArrowRight, Lightbulb, Rocket,
  Heart, CheckCircle
} from 'lucide-react';
import SEO from '../components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const leadership = [
  { name: "Rajesh K. Sharma", title: "Founder & CEO", bio: "15+ years in cybersecurity and telecom. Previously led security architecture at a leading global carrier. Passionate about democratizing enterprise-grade security.", avatar: "RK" },
  { name: "Priya M. Desai", title: "Chief Product Officer", bio: "Former product lead at a top SIEM vendor. Deep expertise in AI/ML-driven threat detection and enterprise product strategy.", avatar: "PM" },
  { name: "Arjun V. Nair", title: "VP Engineering", bio: "Built scalable security platforms processing 10B+ events/day. Expert in distributed systems, real-time analytics, and cloud-native architecture.", avatar: "AV" },
  { name: "Samantha L. Patel", title: "Chief Revenue Officer", bio: "Scaled multiple cybersecurity startups from $0 to $50M ARR. Deep relationships across APAC and EMEA carrier and enterprise markets.", avatar: "SL" },
  { name: "Vikram D. Mehta", title: "Chief Human Resources Officer", bio: "Champion of diverse, high-performance engineering cultures. Built world-class security teams across 4 continents.", avatar: "VD" }
];

const AboutPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vision');

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <SEO title="About SecureSphere | Infuse.AI - Vision, Mission & Leadership" description="Learn about SecureSphere by Infuse.AI - our vision to secure the connected world, our mission, and the leadership team driving telco-grade enterprise security." />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition">
            <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
          </button>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/carriers')} className="text-sm text-neutral-300 hover:text-white transition hidden md:block">Carriers</button>
            <button onClick={() => navigate('/demo')} className="text-sm text-neutral-300 hover:text-white transition hidden md:block">Demo</button>
            <button onClick={() => navigate('/login')} className="px-5 py-2.5 bg-[#F26419] text-white font-semibold rounded-lg text-sm hover:bg-[#F7B731] hover:text-black transition-all">Sign In</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2a1f1a] to-[#1a1a1a]" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-sm font-medium mb-6">About SecureSphere</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">Securing the<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F7B731] to-[#F26419]">Connected World</span></h1>
            <p className="text-lg text-neutral-400 max-w-3xl mx-auto">Born in India, built for the world. SecureSphere by Infuse.AI delivers telco-grade enterprise security that protects every device, every network, and every AI workload.</p>
          </motion.div>
        </div>
      </section>

      {/* Tab Switcher */}
      <section className="sticky top-[73px] z-30 bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3">
            {[
              { id: 'vision', label: 'Vision, Mission & Values' },
              { id: 'leadership', label: 'Meet Our Leadership' }
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`about-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black'
                    : 'text-neutral-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Tab */}
      {activeTab === 'vision' && (
        <>
          {/* Vision Mission Objectives */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { icon: Eye, title: "Our Vision", text: "A world where every enterprise, carrier, and connected device operates within an impenetrable security fabric — powered by AI, delivered at telecom scale.", color: "from-orange-500 to-amber-500" },
                  { icon: Target, title: "Our Mission", text: "To build the most comprehensive, AI-driven security platform that unifies mobile, IoT, automotive, telecom, and enterprise AI workload protection under one intelligent shield.", color: "from-amber-500 to-yellow-500" },
                  { icon: Rocket, title: "Our Objectives", text: "Achieve 99.99% threat detection rate across all vectors. Enable zero-trust security for 1M+ enterprise endpoints. Lead the AI security governance standard globally.", color: "from-yellow-500 to-orange-500" },
                ].map((item, i) => (
                  <motion.div key={item.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="bg-[#242424] border border-white/10 rounded-2xl p-8 hover:border-orange-500/30 transition-all">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5`}>
                      <item.icon className="w-7 h-7 text-black" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-neutral-400 leading-relaxed">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-16 bg-[#242424]/50">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-3xl font-bold text-center mb-12">What Drives Us</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: Shield, label: "Security First", desc: "Every decision starts with security" },
                  { icon: Lightbulb, label: "Innovation", desc: "AI-powered, future-ready solutions" },
                  { icon: Globe, label: "Global Scale", desc: "Carrier-grade, worldwide reach" },
                  { icon: Heart, label: "Trust", desc: "Transparent, reliable partnerships" },
                ].map((v, i) => (
                  <motion.div key={v.label} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center p-6">
                    <v.icon className="w-8 h-8 text-orange-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold mb-1">{v.label}</h4>
                    <p className="text-sm text-neutral-500">{v.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Why SecureSphere */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-3xl font-bold text-center mb-4">Why SecureSphere?</h2>
              <p className="text-neutral-400 text-center max-w-2xl mx-auto mb-12">What sets us apart from every other cybersecurity vendor.</p>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {[
                  "Only platform unifying mobile, IoT, automotive, telecom, AND AI workload security",
                  "Carrier-grade scalability — processing 10B+ security events per day",
                  "AI-native: prompt injection detection, agentic guardrails, model supply chain scanning",
                  "Built for compliance: EU AI Act, NIST AI RMF, ISO 42001, GDPR, DPDP Act",
                  "Real-time WebSocket threat alerts with <8ms response time",
                  "Multi-protocol: SS7, Diameter, GTP, 5G NR, CAN bus, V2X"
                ].map((item, i) => (
                  <motion.div key={item} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-start gap-3 bg-[#242424] rounded-xl p-5 border border-white/10">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-neutral-300">{item}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Leadership Tab */}
      {activeTab === 'leadership' && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="text-3xl font-bold mb-4">Meet Our Leadership</h2>
                <p className="text-neutral-400 max-w-2xl mx-auto">Seasoned leaders from cybersecurity, telecom, and enterprise software — united by the mission to secure the connected world.</p>
              </motion.div>
            </div>

            {/* Founder Card - Featured */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border border-orange-500/30 rounded-3xl p-8 mb-8 max-w-3xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#F7B731] to-[#F26419] flex items-center justify-center text-black font-bold text-3xl flex-shrink-0">{leadership[0].avatar}</div>
                <div className="text-center md:text-left">
                  <h3 className="text-white font-bold text-2xl">{leadership[0].name}</h3>
                  <p className="text-orange-400 font-semibold mb-3">{leadership[0].title}</p>
                  <p className="text-neutral-400 leading-relaxed">{leadership[0].bio}</p>
                </div>
              </div>
            </motion.div>

            {/* Other Leaders */}
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {leadership.slice(1).map((person, i) => (
                <motion.div key={person.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="bg-[#242424] border border-white/10 rounded-2xl p-6 hover:border-orange-500/30 transition-all group">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#F7B731] to-[#F26419] flex items-center justify-center text-black font-bold text-xl flex-shrink-0">{person.avatar}</div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{person.name}</h3>
                      <p className="text-orange-400 text-sm font-medium">{person.title}</p>
                    </div>
                  </div>
                  <p className="text-neutral-400 text-sm leading-relaxed">{person.bio}</p>
                </motion.div>
              ))}
            </div>

            {/* Team Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-16 bg-[#242424] rounded-2xl p-8 border border-white/10">
              <h3 className="text-xl font-bold text-center text-white mb-8">Our Team in Numbers</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: "75+", label: "Security Engineers" },
                  { value: "12", label: "Countries Represented" },
                  { value: "300+", label: "Years Combined Experience" },
                  { value: "4", label: "Global Offices" }
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#F7B731] to-[#F26419]">{stat.value}</p>
                    <p className="text-sm text-neutral-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#F26419]/10 to-[#F7B731]/10 border-y border-orange-500/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Secure Your Enterprise?</h2>
          <p className="text-neutral-400 mb-8">Join leading carriers and enterprises who trust SecureSphere for their most critical security needs.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate('/login')} className="px-8 py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full text-lg hover:shadow-[0_0_40px_rgba(242,100,25,0.5)] transition-all flex items-center gap-2">
              Start Free Trial <ArrowRight className="w-5 h-5" />
            </button>
            <a href="mailto:info@infuse.net.in" className="px-8 py-4 bg-white/5 border border-white/20 text-white font-medium rounded-full text-lg hover:bg-white/10 transition-all">Contact Us</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#242424] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-neutral-500">&copy; 2026 Infuse Technologies. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-sm text-neutral-500 hover:text-white transition">Home</button>
            <button onClick={() => navigate('/carriers')} className="text-sm text-neutral-500 hover:text-white transition">Carriers</button>
            <button onClick={() => navigate('/careers')} className="text-sm text-neutral-500 hover:text-white transition">Careers</button>
            <a href="mailto:info@infuse.net.in" className="text-sm text-neutral-500 hover:text-white transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
